// src/features/organisation/components/competences/CompetenceDetailForm.tsx
import React, { useMemo } from 'react';
import { Award } from 'lucide-react';
import { Competence, CompetenceCreateDto } from '../../../../types/organisation/competences.types';
import { useEmployees } from '../../hooks/useEmployees';
import { apiClient } from '../../../../services/api/client';
import {
  EntityFormLayout,
  EntityFormHeader,
  EntityFormFooter,
  EditableField
} from '../../../../components/common/forms';
import { useEntityForm, FieldConfig } from '../../../../components/common/hooks';

interface CompetenceDetailFormProps {
  competenceId: number | null;
  onClose: () => void;
  onSave?: (competence: Competence) => void;
}

const COMPETENCE_LEVELS = [
  { value: 'Débutant', label: 'Débutant' },
  { value: 'Confirmé', label: 'Confirmé' },
  { value: 'Expérimenté', label: 'Expérimenté' },
  { value: 'Expert', label: 'Expert' },
  { value: 'Certifié', label: 'Certifié' },
  { value: 'Qualifié', label: 'Qualifié' },
];

const CompetenceDetailForm: React.FC<CompetenceDetailFormProps> = ({
  competenceId,
  onClose,
  onSave: onSaveCallback,
}) => {
  const { employees } = useEmployees();
  const [loading, setLoading] = React.useState(!!competenceId);
  const [competence, setCompetence] = React.useState<Competence | null>(null);

  React.useEffect(() => {
    if (competenceId) {
      setLoading(true);
      apiClient.get(`/organisation/competences/${competenceId}`)
        .then((data) => setCompetence(data))
        .finally(() => setLoading(false));
    } else {
      setCompetence(null);
    }
  }, [competenceId]);

  const fields: FieldConfig[] = useMemo(() => [
    {
      name: 'employeeId',
      label: 'Employé',
      type: 'select',
      required: true,
      disabled: !!competenceId,
      options: (employees || [])
        .filter((e: { id: number; firstName: string; lastName: string; employeeCode: string; employeeStatus?: { code: string } }) => e.employeeStatus?.code === 'ACTIVE')
        .map((e: { id: number; firstName: string; lastName: string; employeeCode: string; employeeStatus?: { code: string } }) => ({ value: e.id, label: `${e.firstName} ${e.lastName} (${e.employeeCode})` })),
      render: (value) => {
        const emp = (employees || []).find((e: { id: number; firstName: string; lastName: string; employeeCode: string; employeeStatus?: { code: string } }) => e.id === value);
        return emp ? `${emp.firstName} ${emp.lastName}` : 'Non défini';
      },
    },
    {
      name: 'competenceName',
      label: 'Compétence',
      type: 'text',
      required: true,
      maxLength: 255,
    },
    {
      name: 'competenceLevel',
      label: 'Niveau',
      type: 'select',
      required: true,
      options: COMPETENCE_LEVELS,
      render: (value) => value || 'Non défini',
    },
    {
      name: 'certifyingBody',
      label: 'Organisme de certification',
      type: 'text',
      maxLength: 255,
    },
    {
      name: 'certificationDate',
      label: 'Date de certification',
      type: 'date',
    },
    {
      name: 'expiryDate',
      label: "Date d'expiration",
      type: 'date',
    },
  ], [employees, competenceId]);

  const transformToFormData = (c: Competence | null) => {
    if (!c) return {
      employeeId: 0, competenceName: '', competenceLevel: '',
      certifyingBody: '', certificationDate: '', expiryDate: '',
    };
    return {
      employeeId: c.employeeId || c.employee?.id || 0,
      competenceName: c.competenceName,
      competenceLevel: c.competenceLevel,
      certifyingBody: c.certifyingBody || '',
      certificationDate: c.certificationDate ? c.certificationDate.split('T')[0] : '',
      expiryDate: c.expiryDate ? c.expiryDate.split('T')[0] : '',
    };
  };

  const handleSave = async (formData: Record<string, unknown>) => {
    const data: CompetenceCreateDto = {
      employeeId: formData.employeeId,
      competenceName: formData.competenceName,
      competenceLevel: formData.competenceLevel,
      certifyingBody: formData.certifyingBody || undefined,
      certificationDate: formData.certificationDate || undefined,
      expiryDate: formData.expiryDate || undefined,
    };
    let saved: Competence;
    if (competenceId) {
      saved = await apiClient.put(`/organisation/competences/${competenceId}`, data);
    } else {
      saved = await apiClient.post('/organisation/competences', data);
    }
    onSaveCallback?.(saved as Competence);
    onClose();
    return saved as Competence;
  };

  const {
    formData,
    hasChanges,
    saving,
    errors,
    handleFieldChange,
    startEditing,
    stopEditing,
    isEditing,
    handleSave: onFormSave,
  } = useEntityForm({
    entity: competence,
    fields,
    onSave: handleSave,
    transformToFormData,
  });

  return (
    <EntityFormLayout loading={loading} maxWidth="4xl">
      <EntityFormHeader
        title={competence ? competence.competenceName : 'Nouvelle compétence'}
        subtitle={competence?.employee ? `${competence.employee.firstName} ${competence.employee.lastName}` : undefined}
        icon={Award}
        hasChanges={hasChanges}
        saving={saving}
        onSave={onFormSave}
        onClose={onClose}
      />

      <div className="flex-1 overflow-y-auto p-6">
        {formData && (
          <div className="mb-8">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Informations</h3>
            <div className="grid grid-cols-2 gap-4">
              {fields.map(field => (
                <div key={field.name} className={field.colSpan === 2 ? 'col-span-2' : ''}>
                  <EditableField
                    field={field}
                    value={formData[field.name]}
                    isEditing={isEditing(field.name)}
                    onStartEdit={() => startEditing(field.name)}
                    onStopEdit={() => stopEditing(field.name)}
                    onChange={(value) => handleFieldChange(field.name, value)}
                    error={errors[field.name]}
                  />
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <EntityFormFooter helpText="💡 Double-cliquez sur un champ pour le modifier." />
    </EntityFormLayout>
  );
};

export default CompetenceDetailForm;
